import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppCommon } from '../app-common';
import { ApiCollection } from './api-collection';
import { ApiService } from './api-service';
import { ApiServiceDelegate } from './api-service-delegate';

interface WorkTimeSummaryRecord {
  is_active?: boolean;
  task_name?: string;
}

export interface WorkTimeSummaryState {
  isWorking: boolean;
  totalWorkTimeLabel: string;
  tooltip: string;
}

@Injectable({
  providedIn: 'root'
})
export class WorkTimeSummaryService {
  private summarySubject = new BehaviorSubject<WorkTimeSummaryState>({
    isWorking: false,
    totalWorkTimeLabel: '0 min',
    tooltip: 'Not currently working'
  });
  summary$ = this.summarySubject.asObservable();
  private hasLoaded = false;
  private lastLoadedDate = '';

  constructor(private apiService: ApiService) {}

  loadIfNeeded(){
    const today = this.getLocalDateString(new Date());
    if(this.hasLoaded && this.lastLoadedDate == today){
      return;
    }
    this.refresh();
  }

  refresh(){
    const loggedObject = AppCommon.getloggedInObject();
    const loggedInUserId = loggedObject?.user?.id;
    if(!loggedInUserId){
      return;
    }
    this.hasLoaded = true;
    this.lastLoadedDate = this.getLocalDateString(new Date());
    this.getActiveSummary(loggedInUserId);
    this.getTotalSummary(loggedInUserId);
  }

  private getActiveSummary(loggedInUserId: number){
    let d: ApiServiceDelegate = {
      Data: {},
      Method: 'GET',
      Action: ApiCollection.WORK_TIME + this.getTodayWorkTimeSummaryQuery(loggedInUserId, 'true'),
      Success: (e: any) => {
        const records = this.getWorkTimeResults(e);
        const isWorking = (Number(e?.count) > 0) || records.some(record => record.is_active === true);
        const activeRecord = records.find(record => record.is_active === true);
        const tooltip = isWorking
          ? (activeRecord?.task_name ? 'Working on: ' + activeRecord.task_name : 'Working')
          : 'Not currently working';
        this.summarySubject.next({
          ...this.summarySubject.value,
          isWorking,
          tooltip
        });
        return '';
      },
      Error: () => {
        this.summarySubject.next({
          ...this.summarySubject.value,
          isWorking: false,
          tooltip: 'Not currently working'
        });
        return '';
      },
    };
    this.apiService.callService(d);
  }

  private getTotalSummary(loggedInUserId: number){
    let d: ApiServiceDelegate = {
      Data: {},
      Method: 'GET',
      Action: ApiCollection.WORK_TIME + this.getTodayWorkTimeSummaryQuery(loggedInUserId, ''),
      Success: (e: any) => {
        this.summarySubject.next({
          ...this.summarySubject.value,
          totalWorkTimeLabel: this.formatWorkTime(e?.total_work_time)
        });
        return '';
      },
      Error: () => {
        return '';
      },
    };
    this.apiService.callService(d);
  }

  private getTodayWorkTimeSummaryQuery(loggedInUserId: number, isActive: string){
    const today = this.getLocalDateString(new Date());
    return '?task=&created_by=' + encodeURIComponent(loggedInUserId)
      + '&is_active=' + encodeURIComponent(isActive)
      + '&start_date='
      + '&start_date_from=' + encodeURIComponent(today)
      + '&start_date_to=' + encodeURIComponent(today);
  }

  private getLocalDateString(date: Date){
    const pad = (value: number) => String(value).padStart(2, '0');
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  private getWorkTimeResults(response: any){
    if(Array.isArray(response)){
      return response as WorkTimeSummaryRecord[];
    }
    return (response?.results || []) as WorkTimeSummaryRecord[];
  }

  private formatWorkTime(value: string){
    const parts = String(value || '00:00:00').split(':').map(part => Number(part));
    if(parts.length != 3 || parts.some(part => isNaN(part))){
      return '0 min';
    }
    const hours = parts[0];
    const minutes = parts[1];
    const seconds = Math.floor(parts[2]);
    if(hours > 0){
      return hours + ' ' + (hours == 1 ? 'hour' : 'hours') + (minutes > 0 ? ' ' + minutes + ' min' : '');
    }
    if(minutes > 0){
      return minutes + ' min' + (seconds > 0 ? ' ' + seconds + ' sec' : '');
    }
    if(seconds > 0){
      return seconds + ' sec';
    }
    return '0 min';
  }
}
